import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import api from '../api';

const Header = () => {
    // --- States ---
    const [menus, setMenus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
    const searchRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    // --- Inline CSS ---
    const styles = {
        searchContainer: { position: 'relative', maxWidth: '300px' },
        dropdown: {
            position: 'absolute', top: '100%', right: '0', width: '350px',
            backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            zIndex: '9999', marginTop: '12px', maxHeight: '400px', overflowY: 'auto',
            border: '1px solid #e1e1e1', padding: '0',
        },
        resultList: { listStyle: 'none', padding: '0', margin: '0' },
        resultItem: {
            padding: '12px 15px', cursor: 'pointer', borderBottom: '1px solid #f1f1f1',
            display: 'flex', flexDirection: 'column', transition: 'background 0.2s',
        },
        resultTitle: { fontSize: '14px', fontWeight: '600', color: '#222', marginBottom: '2px' },
        resultType: { fontSize: '11px', color: '#007bff', textTransform: 'uppercase', fontWeight: '700' },
        noResults: { padding: '20px', textAlign: 'center', color: '#666', fontSize: '14px' },
        loadingSpinner: {
            width: '15px', height: '15px', border: '2px solid #f3f3f3', borderTop: '2px solid #3498db',
            borderRadius: '50%', animation: 'spin 1s linear infinite', display: 'inline-block', marginRight: '10px'
        }
    };

    // Function to close mobile menu
    const closeMobileMenu = () => {
        document.body.classList.remove('mobile-menu-visible');

        // Also close any open dropdowns in mobile menu
        const dropdowns = document.querySelectorAll('.mobile-menu li.dropdown > ul, .mobile-menu li.dropdown > .megamenu');
        dropdowns.forEach(menu => {
            menu.style.display = 'none';
        });

        const dropdownBtns = document.querySelectorAll('.mobile-menu .dropdown-btn');
        dropdownBtns.forEach(btn => btn.classList.remove('open'));
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 992);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Fetch Menus for navigation
    useEffect(() => {
        const fetchMenus = async () => {
            try {
                const response = await api.get(`${process.env.REACT_APP_API_URL}/api/menus`);
                setMenus(response.data.data);
            } catch (err) {
                setError('Failed to load menus');
            } finally {
                setLoading(false);
            }
        };
        fetchMenus();
    }, []);

    // Close mobile menu when location changes (route changes)
    useEffect(() => {
        closeMobileMenu();
    }, [location]);

    // Search Logic with Debounce
    useEffect(() => {
        const fetchSearchResults = async () => {
            if (searchQuery.length < 1) {
                setSearchResults([]);
                setShowDropdown(false);
                return;
            }

            setIsSearching(true);
            try {
                const response = await api.get(`/api/global-search?q=${searchQuery}`);
                setSearchResults(response.data);
                setShowDropdown(true);
            } catch (err) {
                console.error("Search error:", err);
            } finally {
                setIsSearching(false);
            }
        };

        const timer = setTimeout(fetchSearchResults, 400);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const handleResultClick = (path) => {
        setSearchQuery('');
        setShowDropdown(false);
        closeMobileMenu(); // Close mobile menu when search result is clicked

        if (path.startsWith('http')) {
            window.location.href = path;
        } else {
            navigate(path);
        }
    };

    // jQuery Mobile Menu logic
    useEffect(() => {
        // Mobile menu toggler functionality
        const mobileNavToggler = document.querySelector('.mobile-nav-toggler');
        const closeBtn = document.querySelector('.mobile-menu .close-btn');
        const menuBackdrop = document.querySelector('.mobile-menu .menu-backdrop');

        const openMobileMenu = (e) => {
            if (e) e.preventDefault();
            document.body.classList.add('mobile-menu-visible');
        };

       

        if (closeBtn) {
            closeBtn.addEventListener('click', closeMobileMenu);
        }

        if (menuBackdrop) {
            menuBackdrop.addEventListener('click', closeMobileMenu);
        }

        // Close mobile menu on escape key
        const handleEscapeKey = (e) => {
            if (e.key === 'Escape' && document.body.classList.contains('mobile-menu-visible')) {
                closeMobileMenu();
            }
        };
        document.addEventListener('keydown', handleEscapeKey);

        // Initialize mobile menu dropdowns
        const initMobileMenuDropdowns = () => {
            const dropdownBtns = document.querySelectorAll('.mobile-menu .dropdown-btn');

            dropdownBtns.forEach(btn => {
                btn.addEventListener('click', function (e) {
                    e.preventDefault();

                    const parentLi = this.closest('li.dropdown');
                    if (!parentLi) return;

                    const currentMenu =
                        parentLi.querySelector(':scope > ul, :scope > .megamenu');

                    if (!currentMenu) return;

                    const isOpen = currentMenu.style.display === 'block';

                    // ✅ CLOSE ONLY OTHER DROPDOWNS
                    document
                        .querySelectorAll('.mobile-menu li.dropdown > ul, .mobile-menu li.dropdown > .megamenu')
                        .forEach(menu => {
                            menu.style.display = 'none';
                        });

                    document
                        .querySelectorAll('.mobile-menu .dropdown-btn')
                        .forEach(btn => btn.classList.remove('open'));

                    // ✅ TOGGLE CURRENT
                    if (!isOpen) {
                        currentMenu.style.display = 'block';
                        this.classList.add('open');
                    }
                });
            });
        };

        // Add dropdown buttons to mobile menu
        const setupMobileMenu = () => {
            const mobileMenuOuter = document.querySelector('.mobile-menu .menu-outer');
            if (!mobileMenuOuter) return;

            // Clear existing dropdown buttons first
            const existingBtns = mobileMenuOuter.querySelectorAll('.dropdown-btn');
            existingBtns.forEach(btn => btn.remove());

            // Add dropdown buttons for mobile menu - now looking for both dropdown and link-dropdown
            const dropdownItems = mobileMenuOuter.querySelectorAll('li.dropdown, li.link-dropdown');
            dropdownItems.forEach(item => {
                if (!item.querySelector('.dropdown-btn')) {
                    const dropdownBtn = document.createElement('div');
                    dropdownBtn.className = 'dropdown-btn';
                    dropdownBtn.innerHTML = '<span class="fa fa-angle-right"></span>';
                    item.appendChild(dropdownBtn);
                }
            });

            initMobileMenuDropdowns();

            // Add click event listeners to all links in mobile menu to close the menu
            const mobileLinks = mobileMenuOuter.querySelectorAll('a');
            mobileLinks.forEach(link => {
                link.addEventListener('click', (e) => {
                    // Only close if it's not a dropdown toggle link
                    if (!link.closest('.dropdown') || !link.closest('.link-dropdown')) {
                        closeMobileMenu();
                    }
                });
            });
        };

        // Run setup after a short delay
        const timer = setTimeout(setupMobileMenu, 100);

        return () => {
            clearTimeout(timer);
            if (mobileNavToggler) {
                mobileNavToggler.removeEventListener('click', openMobileMenu);
            }
            if (closeBtn) {
                closeBtn.removeEventListener('click', closeMobileMenu);
            }
            if (menuBackdrop) {
                menuBackdrop.removeEventListener('click', closeMobileMenu);
            }
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [menus, isMobile]);

   const shouldHideDiv = false;

    const API_BASE_URL = process.env.REACT_APP_API_URL;

    const renderMegaMenuContent = (menu, isMobileView = false) => {
        if (!menu.columns || menu.columns.length === 0) return null;
        return (
            <div className="megamenu">
                <div className="row justify-content-center">
                    {menu.columns.map((column) => (
                        <div key={column.column_order} className={column.column_class}>
                            {column.items.map((item) => {
                                switch (item.type) {
                                    case 'heading':
                                        return item.title && item.title.trim() !== '' ? (
                                            <h3 key={item.id} className="megamenu-heading mb-2">
                                                {item.title}
                                            </h3>
                                        ) : null;
                                    case 'link': return (
                                        <ul key={item.id} className="right-seperator">
                                            <li>
                                                <Link
                                                    to={item.url || '#'}
                                                    onClick={() => isMobileView && closeMobileMenu()}
                                                >
                                                    {item.title}
                                                </Link>
                                            </li>
                                        </ul>
                                    );
                                    case 'image':
                                        const imgSrc = item.content?.startsWith('http') ? item.content : `${API_BASE_URL}/public/${item.content}`;
                                        return <div key={item.id} className="header-image-wrapper"><img src={imgSrc} alt={item.title ?? 'header_image'} style={{ width: '100%' }} /></div>;
                                    case 'text': return <div key={item.id} className="text-content">{item.content}</div>;
                                    default: return null;
                                }
                            })}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Recursive function to render menu items with children
    const renderMenuItem = (menu, isMobileView = false) => {
        const hasChildren = menu.children && menu.children.length > 0;
        const isMegaMenu = menu.type === 'megamenu' || menu.type === 'mega-menu';
        const isDropdown = hasChildren && !isMegaMenu;
        const isCampus = menu.title?.toLowerCase().includes('campus');

        if (isDropdown) {
            return (
                <li key={menu.id} className={isMobileView ? "dropdown" : "link-dropdown"}>
                    {isMobileView ? (
                        // Mobile view - simple anchor
                        <a href={menu.url || '#'}>{menu.title}</a>
                    ) : (
                        // Desktop view - with dropdown arrow
                        <a href={menu.url || '#'} className="link-dropdown-toggle">
                            {menu.title} <i className="fa fa-caret-down ms-1"></i>
                        </a>
                    )}
                    <ul className={isMobileView ? "" : "top-dropdown"} style={isMobileView ? {} : { display: 'none' }}>
                        {menu.children.map(child => renderMenuItem(child, isMobileView))}
                    </ul>
                </li>
            );
        }

        if (isMegaMenu) {
            return (
                <li key={menu.id} className={`dropdown ${isCampus ? 'campus-dropdown' : ''}`}>
                    <a href={menu.url || '#'}>{menu.title}</a>
                    {renderMegaMenuContent(menu, isMobileView)}
                </li>
            );
        }

        // Regular menu item - check if it has children but is not a dropdown or mega menu
        if (hasChildren) {
            return (
                <li key={menu.id} className={menu.type === 'top-menu' ? 'menu_highlighter' : ''}>
                    <span>{menu.title}</span>
                    <ul>
                        {menu.children.map(child => renderMenuItem(child, isMobileView))}
                    </ul>
                </li>
            );
        }

        return (
            <li key={menu.id} className={menu.type === 'top-menu' ? 'menu_highlighter' : ''}>
                <Link
                    to={menu.url || '#'}
                    onClick={() => isMobileView && closeMobileMenu()}
                >
                    {menu.title}
                </Link>
            </li>
        );
    };

    if (loading || error) return null;

    const hasTopMenu = menus.some(menu => menu.type === 'top-menu');
    const hasSmallMenu = menus.some(menu => menu.type === 'small-menu');
    const hasMainMenu = menus.some(menu => menu.type === 'main-menu' || menu.type === 'megamenu' || menu.type === 'mega-menu');

    // Get filtered menus based on view
    const getFilteredMenus = (forMobile) => {
        if (forMobile) {
            return menus.filter(m =>
                m.type === 'main-menu' ||
                m.type === 'megamenu' ||
                m.type === 'mega-menu' ||
                m.type === 'top-menu'
            );
        } else {
            return menus.filter(m =>
                m.type === 'main-menu' ||
                m.type === 'megamenu' ||
                m.type === 'mega-menu'
            );
        }
    };
    // ✅ MOBILE MENU ORDER FUNCTION
    const getMobileOrderedMenus = () => {
        const mainMenus = menus.filter(
            m =>
                m.type === 'main-menu' ||
                m.type === 'megamenu' ||
                m.type === 'mega-menu'
        );

        const topMenus = menus.filter(m => m.type === 'top-menu');

        const smallMenus = menus.filter(m => m.type === 'small-menu');

        // ORDER: main → top → small
        return [...mainMenus, ...topMenus, ...smallMenus];
    };

    return (
        <header className="main-header header-style-one">
            {hasTopMenu && (
                <div className="header-top">
                    <div className="auto-container">
                        <div className="outer-box" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div className="header-top-left">
                                <div className="quick-link-box">
                                    <div className="link-box">
                                        <ul>
                                            {menus
                                                .filter(m => m.type === 'top-menu')
                                                .map(menu => renderMenuItem(menu, false))}
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div ref={searchRef} style={styles.searchContainer}>
                                <form className="search-form" onSubmit={(e) => e.preventDefault()}>
                                    <label htmlFor="search" style={{ width: '100%', position: 'relative' }}>
                                        <input
                                            required autoComplete="off" placeholder="Search pages..." id="search" type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            onFocus={() => searchQuery.length >= 1 && setShowDropdown(true)}
                                        />
                                        <div className="icon">
                                            {isSearching ? <div style={styles.loadingSpinner}></div> : (
                                                <svg strokeWidth="2" stroke="currentColor" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="swap-on">
                                                    <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinejoin="round" strokeLinecap="round"></path>
                                                </svg>
                                            )}
                                        </div>
                                        {searchQuery && (
                                            <button type="reset" className="close-btn" onClick={() => setSearchQuery('')}>
                                                <svg viewBox="0 0 20 20" className="h-5 w-5" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" fill="#999"></path>
                                                </svg>
                                            </button>
                                        )}
                                    </label>
                                </form>

                                {showDropdown && (
                                    <div style={styles.dropdown}>
                                        {searchResults.length > 0 ? (
                                            <ul style={styles.resultList}>
                                                {searchResults.map((result, index) => (
                                                    <li key={index} style={styles.resultItem} onClick={() => handleResultClick(result.path)}
                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                                        <span style={styles.resultTitle}>{result.title}</span>
                                                        <span style={styles.resultType}>{result.type}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div style={styles.noResults}>No matches found for "{searchQuery}"</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!shouldHideDiv && (
                <div className="header">
                    <div className="auto-container">
                        <div className="outer-box">
                            <div className="header-middle">
                                <div className={`main-logo-box ${!hasMainMenu ? "mt-5 pt-5" : ""}`}>
                                    <Link
                                        to="/"
                                        onClick={() => isMobile && closeMobileMenu()}
                                    >
                                        <img src="/assets/images/care-logo-half-white.png" alt="care-logo" />
                                    </Link>
                                </div>
                                <div className="nav-outer style1 clearfix">
                                    <nav className="right-menu">
                                        <ul className={`d-flex justify-content-end gap-4 ${!hasSmallMenu ? "pt-5" : "pt-3"}`}>
                                            {menus
                                                .filter(m => m.type === 'small-menu')
                                                .map(menu => renderMenuItem(menu, false))}
                                        </ul>
                                    </nav>
                                    <div
                                        className="mobile-nav-toggler"
                                        onClick={() => {
                                            document.body.classList.add('mobile-menu-visible');
                                        }}
                                        >

                                        <div className="inner">
                                            <span className="icon-bar"></span><span className="icon-bar"></span><span className="icon-bar"></span>
                                        </div>
                                    </div>

                                    {/* DESKTOP MENU ONLY */}
                                    {!isMobile && (
                                        <nav className="main-menu style1 navbar-expand-md navbar-light">
                                            <div
                                                className="collapse navbar-collapse show clearfix"
                                                id="navbarSupportedContent"
                                            >
                                                <ul className="navigation clearfix">
                                                    {getFilteredMenus(false).map(menu =>
                                                        renderMenuItem(menu, false)
                                                    )}
                                                </ul>
                                            </div>
                                        </nav>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Sticky Header */}
            <div className="sticky-header d-md-none">
                <div className="container">
                    <div className="sticky-header__inner clearfix">
                        <div className="row">
                            <div className="col-md-2">
                                <div className="logo">
                                    <a href="#" className="img-responsive">
                                        <img src="https://generalcwdbucket.s3.us-east-1.amazonaws.com/assets/images/resources/care-logo.png" alt="care-logo" title="" style={{ width: '200px' }} />
                                    </a>
                                </div>
                            </div>
                            <div className="col-md-10">
                                <div className="right-col">
                                    <nav className="main-menu clearfix">
                                        {/* Menu will be copied via jQuery */}
                                    </nav>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* MOBILE MENU ONLY */}
            {isMobile && (
            <div className="mobile-menu">
                <div className="menu-backdrop"></div>
                <div className="close-btn"><span className="icon fa fa-times-circle"></span></div>
                <nav className="menu-box">
                    <div className="nav-logo">
                        <Link
                            to="/"
                            onClick={closeMobileMenu}
                        >
                            <img
                                src="/assets/images/care-logo-white.png"
                                alt="CARE Logo"
                                title="CARE"
                                style={{ width: "170px" }}
                            />
                        </Link>
                    </div>
                    <div className="menu-outer">
                        <ul className="navigation clearfix">
                            {getMobileOrderedMenus().map(menu => renderMenuItem(menu, true))}
                        </ul>

                    </div>
                </nav>
            </div>
            )}
        </header>
    );
};

export default Header;